/* eslint-disable @typescript-eslint/unbound-method */
/* eslint-disable prefer-const */
import { AnnouncementInfo } from "../contracts/announcementInfo";

import { ActivityBridge } from "./activityBridge";
import ActivityType from "../entities/activityType";
import WindowType from "../entities/windowType";
import { EventHandlerCallback } from "../contracts/contracts";
import { WindowDefinition } from "../entities/windowDefinition";
import {
    ActivityContextChangedEventContext,
    ActivityStatusChangeEventContext,
    EntityEvent,
    EntityEventContext,
    EntityEventType,
} from "../contracts/entityEvent";
import { WindowFactory } from "../contracts/windowFactory";
import Activity from "../entities/activity";
import ActivityWindow from "../entities/activityWindow";
import { WindowBounds } from "../contracts/windowBounds";
import {
    ActivityGwMessage,
    ActivityTypeGwMessage,
    ActivityTypeGwMessageEntity,
    GwMessage,
    PeerCreatedGwMessage,
    PeerFactoryGwMessage,
    PeerFactoryGwMessageEntity,
    PeerRequestedGwMessage,
} from "./gw3BridgeMessages";
import { ActivityDescriptor } from "../entities/activityDescriptor";
import { CloneOptions } from "../entities/cloneOptions";
import { Glue42 } from "../../../glue";

import { ActivityStatus } from "../contracts/activityStatus";
import { ActivityConfig } from "../activityConfig";
import { Glue42Core } from "@glue42/core";

import { Glue42 as glue42 } from "../../../glue";
type OverrideTypeDefinition = glue42.Activities.OverrideTypeDefinition;

const gwMessageSuccess = "success";
const gwMmessageError = "error";

const gwMessageAddActivityTypes = "add-types";
const gwMmessageActivityTypesAdded = "types-added";
const gwMessageRemoveActivityTypes = "remove-types";
const gwMessageActivityTypesRemoved = "types-removed";

const gwMessageActivityCreated = "created";
const gwMessageActivityDestroyed = "destroyed";
const gwMessageActivityInitiated = "initiated";
const gwMmessageJoinActivity = "join-activity";
const gwMessageJoinedActivity = "joined";
const gwMessageActivityJoined = "activity-joined";
const gwMessageLeaveActivity = "leave-activity";
const gwMessageActivityLeft = "left";
const gwNmessageMergeActivities = "merge";
const gwMessageSplitActivities = "split";
const gwMessageChildrenChanged = "children-changed";
const gwMessageOwnerChanged = "owner-changed";

const gwMessageAddPeerFactories = "add-peer-factories";
const gwMessagePeerFactoriesAdded = "peer-factories-added";
const gwMessageRemovePeerFactories = "remove-peer-factories";
const gwMessagePeerFactoriesRemoved = "peer-factories-removed";

const gwMessageCreateActivity = "create";
const gwMessageCreatePeer = "create-peer";
const gwMessagePeerRequested = "peer-requested";
const gwMessageReady = "ready";
const gwMessagePeerCreated = "peer-created";

const gwMessageDestroyActivity = "destroy";
const gwMessageDisposePeer = "dispose-peer";
const gwMessageDestroyPeer = "destroy-peer";

declare let window: any;
declare let process: any;

export default class GW3Bridge implements ActivityBridge {

    private static activityTypeGwMessageEntityToActivityType(
        entity: ActivityTypeGwMessageEntity,
        description: string): ActivityType {

        const nameToWindowType =
            (windowName: any) =>
                new WindowType(
                    windowName,
                    undefined);

        return new ActivityType(
            entity.name,
            entity.owner_type && nameToWindowType(entity.owner_type),
            entity.helper_types && entity.helper_types.map(nameToWindowType),
            description);
    }

    private static peerFactoryGwMessageEntityToWindowType(entity: PeerFactoryGwMessageEntity): WindowType {
        return new WindowType(
            entity.peer_type,
      /* #deleteme TODO: review - appmanager configuration? */(_) => undefined);
    }

    private static activityGwMessageToActivity(msg: ActivityGwMessage, status: ActivityStatus): Activity {
        const ownerId = msg.owner !== undefined ? msg.owner.peer_id : msg.owner_id;
        return new Activity(
            msg.activity_id,
            msg.activity_type,
            status,
            msg.context_snapshot,
            ownerId);
    }

    private static activityToActivityStatusChangeEvent(act: Activity) {
        return new EntityEvent<Activity>(act, new ActivityStatusChangeEventContext(act.status, undefined));
    }

    private _connection: Glue42.Connection.API;
    private _logger: any;
    private _contexts: Glue42.Contexts.API;
    private _windows: Glue42.Windows.API;

    private _gw3Session: Glue42Core.Connection.GW3DomainSession;

    // callbacks

    private _activityChangeCallbacks: EventHandlerCallback<EntityEvent<Activity>>[] = [];
    private _activityTypeStatusChangeCallbacks: EventHandlerCallback<EntityEvent<ActivityType>>[] = [];
    private _activityWindowChangeCallbacks: EventHandlerCallback<EntityEvent<ActivityWindow>>[] = [];
    private _windowTypeStatusChangeCallbacks: EventHandlerCallback<EntityEvent<WindowType>>[] = [];

    // state tracking
    private _peerIdAndFactoryIdToPeerType: any = {};
    private _peerFactoriesRegisteredByUs: { [index: string]: WindowFactory } = {};
    private _gw3Subscriptions: any[] = [];
    private _contextSubscriptions: any = {};
    private _activityTypesInitiatedFromMe: any = {};

    private _sessionJoinedPromise: Promise<GW3Bridge>;
    private _sessionJoinedPromiseResolve: (bridge: GW3Bridge) => void;
    private _activityJoinedPromise: Promise<object>;
    private _activityJoinedPromiseResolve: (obj: object) => void;
    private _config: ActivityConfig;

    constructor(config: ActivityConfig) {
        this._config = config;
        this._connection = config.connection;
        this._logger = config.logger;
        this._contexts = config.contexts;
        this._windows = config.windows;

        this._sessionJoinedPromise = new Promise<GW3Bridge>((resolve /* , reject */) => {
            this._sessionJoinedPromiseResolve = resolve;
        });

        this._activityJoinedPromise = new Promise<object>((resolve /* , reject */) => {
            this._activityJoinedPromiseResolve = resolve;
        });

        if (!this._config.activityId) {
            this._activityJoinedPromiseResolve({});
        }

        this._gw3Session = this._connection.domain(
            "activity",
            ["joined", "initiated", "peer-created", "token"]);

        if (typeof window !== "undefined") {

            const glue42gd = (window).glue42gd;

            // only execute if in activity
            if (glue42gd && glue42gd.activityInfo) {
                // G4E-1343
                if (typeof glue42gd.addRefreshHandler === "function") {

                    glue42gd.addRefreshHandler((success: any, error: any) => {
                        this._gw3Session
                            .send(
                                {
                                    type: "reload"
                                })
                            .then(
                                (msg: any) => {
                                    if (!msg.token) {
                                        error("Expected gateway token for refreshing.");
                                        return;
                                    }
                                    try {
                                        glue42gd.setGWToken(msg.token as string);
                                    } catch (e) {
                                        error(e.message || e);
                                        return;
                                    }
                                    success();
                                },
                                error);
                    });
                }

                // JP_GD3-598
                if (glue42gd && typeof glue42gd.addWillNavigateHandler === "function") {

                    glue42gd.addWillNavigateHandler((success: any, error: any) => {
                        this._gw3Session
                            .send(
                                {
                                    type: "reload"
                                })
                            .then(
                                (msg: any) => {
                                    if (!msg.token) {
                                        error("Expected gateway token for refreshing.");
                                        return;
                                    }
                                    try {
                                        glue42gd.setGWToken(msg.token as string);
                                    } catch (e) {
                                        error(e.message || e);
                                        return;
                                    }
                                    success();
                                },
                                error);
                    });
                }
            }
        }
    }

    public get bridgeType(): "GW3" {
        return "GW3";
    }

    public init() {
        // activity types
        this.forwardActivityTypeMessagesToStatusEventHandlers();

        // activities
        this.subscribe<ActivityGwMessage>(
            gwMessageActivityCreated,
            this.handleActivityCreatedMessage);

        this.subscribe<ActivityGwMessage>(
            gwMessageActivityDestroyed,
            this.handleActivityDestroyedMessage);

        this.forwardActivityMessagesToStatusEventHandlers();

        this.forwardActivityCreatedAndJoinedActivityToActivityWindowEventHandlers();

        // window types / peer factories

        // NB: this forward must happen before the handlePeerFactoriesRemoved subscription
        // since the latter clears state which is used by peerFactoryIdAndOwnerIdToWindowType
        this.forwardPeerFactoryMessagesToStatusEventHandlers();

        this.forwardPeerFactoryMessagesToPeerFactoryRequests();

        this.subscribe(
            gwMessagePeerFactoriesAdded,
            this.handlePeerFactoriesAdded);

        this.subscribe(
            gwMessagePeerFactoriesRemoved,
            this.handlePeerFactoriesRemoved);

        // activity windows

        this.forwardActivityWindowMessagesToEventHandlers();
        //

        this.subscribe(
            gwMessageDisposePeer,
      /* (msg)*/() => {
                if (this._config.disposeRequestHandling === "dispose") {
                    this.dispose();
                    return;
                }
                if (this._config.disposeRequestHandling === "exit") {
                    if (this._windows && typeof this._windows.my() !== "undefined") {
                        this._windows.my().close();
                        return;
                    }
                    if (typeof window !== "undefined" && typeof (window).close === "function") {
                        window.close();
                        return;
                    }
                    if (typeof process !== "undefined" && typeof (process).exit === "function") {
                        process.exit();
                        return;
                    }
                }
            });

        // start domain join handshake
        this._gw3Session.onJoined(() => {

            if (this._config.mode === "trackMyOnly" ||
                this._config.mode === "trackMyTypeAndInitiatedFromMe") {
                this._sessionJoinedPromiseResolve(this);
            } else {
                this._gw3Session
                    .send(
                        {
                            type: "subscribe",
                            activity_types:
                                (this._config.mode === "trackAll" ? [] :
                                    this._config.mode === "trackTypes" ? this._config.typesToTrack : [])
                        })
                    .then(() => {
                        this._sessionJoinedPromiseResolve(this);
                    });
            }
        });
        this._gw3Session.join();
    }

    public dispose() {
        this._gw3Subscriptions.forEach((sub) => sub && this._connection.off(sub));
        this._gw3Subscriptions.length = 0;
    }

    // ActivityBridge interface

    public ready(): Promise<object> {
        return Promise.all([this._sessionJoinedPromise, this._activityJoinedPromise]);
    }

    public initReady(): Promise<ActivityBridge> {
        return this._sessionJoinedPromise;
    }

    public onActivityTypeStatusChange(callback: EventHandlerCallback<EntityEvent<ActivityType>>) {
        this._activityTypeStatusChangeCallbacks.push(callback);
    }

    public registerActivityType(
        activityTypeName: string,
        ownerWindow: WindowDefinition,
        helperWindows?: WindowDefinition[],
        config?: any,
        description?: string): Promise<ActivityType> {

        const entity: ActivityTypeGwMessageEntity = {};
        entity.name = activityTypeName;
        const toActivityPeerConfig = (windowDefinition: WindowDefinition) => ({ type: windowDefinition.type, name: windowDefinition.name, configuration: windowDefinition });

        entity.owner_type = toActivityPeerConfig(ownerWindow);
        entity.helper_types = helperWindows.map(toActivityPeerConfig);

        return this._gw3Session
            .send(
                {
                    type: gwMessageAddActivityTypes,
                    types: [entity]
                })
            .then(() => {
                const activityType = GW3Bridge.activityTypeGwMessageEntityToActivityType(entity, description);
                this.invokeCallbacks(
                    this._activityTypeStatusChangeCallbacks,
                    new EntityEvent<ActivityType>(
                        activityType,
                        new EntityEventContext(EntityEventType.Added)),
                    gwMessageAddActivityTypes);
                return activityType;
            });
    }

    public unregisterActivityType(activityTypeName: string): Promise<void> {
        return this._gw3Session
            .send(
                {
                    type: gwMessageRemoveActivityTypes,
                    types: [activityTypeName]
                })
            .then(() => {
                const activityType = new ActivityType(activityTypeName, undefined, undefined, undefined);
                this.invokeCallbacks(
                    this._activityTypeStatusChangeCallbacks,
                    new EntityEvent<ActivityType>(
                        activityType,
                        new EntityEventContext(EntityEventType.Removed)),
                    gwMessageAddActivityTypes);
            });
    }

    public onWindowTypeStatusChange(callback: EventHandlerCallback<EntityEvent<WindowType>>): void {
        this._windowTypeStatusChangeCallbacks.push(callback);
    }

    // NB: in GW3, peer factories have ids separate from the ids of the peers they create
    // in this implementation we keep the two equal for simplicity
    // NB: we assume only one peer will register a peer factory for a given type; otherwise the
    // add/remove window types event logic will get messy
    public registerWindowFactory(
        windowType: string,
        factory: WindowFactory,
        parameters?: any): Promise<void> {
        if (this._peerFactoriesRegisteredByUs[windowType]) {
            return Promise.reject(new Error(`Factory for windowType ${windowType} already registered.`));
        }

        this._peerFactoriesRegisteredByUs[windowType] = factory;

        // #deleteme TODO: review success/failure code paths
        // #deleteme TODO: review - should we create the window type here?
        const entity = {
            id: windowType,
            peer_type: windowType,
            configuration: parameters
        };
        return this._gw3Session.send({
            type: gwMessageAddPeerFactories,
            factories: [entity]
        })
            .then(() => {
                this.invokeCallbacks(
                    this._windowTypeStatusChangeCallbacks,
                    new EntityEvent(
                        GW3Bridge.peerFactoryGwMessageEntityToWindowType(entity),
                        new EntityEventContext(EntityEventType.Added)),
                    gwMessageAddPeerFactories);
            })
            .catch(() => {
                delete this._peerFactoriesRegisteredByUs[windowType];
            });
    }

    public unregisterWindowFactory(windowType: string): Promise<void> {
        const factory = this._peerFactoriesRegisteredByUs[windowType];
        if (!factory) {
            return Promise.reject(new Error(`Factory for windowType ${windowType} not registered.`));
        }

        delete this._peerFactoriesRegisteredByUs[windowType];

        return this._gw3Session.send({
            type: gwMessageRemovePeerFactories,
            factory_ids: [windowType]
        }).then(() => {
            this.invokeCallbacks(
                this._windowTypeStatusChangeCallbacks,
                new EntityEvent(
                    new WindowType(windowType, undefined),
                    new EntityEventContext(EntityEventType.Removed)),
                gwMessageAddPeerFactories);
        });
    }

    public onActivityStatusChange(callback: EventHandlerCallback<EntityEvent<Activity>>) {
        this._activityChangeCallbacks.push(callback);
    }

    public initiateActivity(activityType: string, context?: object, configuration?: WindowDefinition[] | OverrideTypeDefinition): Promise<string> {
        const initiateMsg: any = {
            type: gwMessageCreateActivity,
            activity_type: activityType,
            initial_context: context,
        };

        if (this.isOverrideTypeDefinition(configuration)) {
            initiateMsg.types_override = {
                owner_type: { type: configuration.owner.type, name: configuration.owner.name, configuration: configuration.owner },
                helper_types: configuration.helpers && configuration.helpers.map((wd) => ({ type: wd.type, name: wd.name, configuration: wd }))
            };
        } else {
            initiateMsg.configuration = configuration && configuration.map((wd) => ({ type: wd.type, name: wd.name, configuration: wd }));
        }

        return this.sendCreateAndMapResultingMessagesToPromise(
            initiateMsg,
            gwMessageActivityInitiated,
            (msg, requestId) => msg.request_id === requestId,
            gwMessageActivityCreated,
            (msg, requestId, initMsg) => msg.activity_id === initMsg.activity_id,
            gwMessageActivityDestroyed,
            (msg, requestId, initMsg) => msg.activity_id === initMsg.activity_id,
            (msg) => msg.activity_id,
            null).then((id) => {
                if (this._config.mode === "trackMyTypeAndInitiatedFromMe") {
                    if (!this._activityTypesInitiatedFromMe[activityType]) {
                        this._activityTypesInitiatedFromMe[activityType] = true;

                        return this._gw3Session
                            .send(
                                {
                                    type: "subscribe",
                                    activity_types: [activityType]
                                })
                            .then(() => {
                                return id;
                            });
                    }
                }
                return id;
            });
    }

    public stopActivity(activity: Activity): Promise<boolean> {
        return this._gw3Session.send({
            type: gwMessageDestroyActivity,
            activity_id: activity.id,
            reason_uri: "com.tick42.glue.activity.constants.destroyReason.general",
            reason: "Destroying activity"
        }).then((_) => true);
    }

    public updateActivityContext(activity: Activity, context: any, fullReplace: boolean, removedKeys?: string[]): Promise<void> {
        if (fullReplace) {
            // this will currently fail on GW3
            return this._contexts.set(activity.id, context);
        } else {
            removedKeys = removedKeys || [];
            for (const x of removedKeys) {
                context[x] = null;
            }
            return this._contexts.update(activity.id, context);
        }
    }

    public announceWindow(windowType?: string, activityWindowId?: string) {
        // in the GW3 case we are only joined to an activity after we announce ourselves
        // so all announcement calls should go in 'registerWindow', not here
        // (registerWindow registers a window that's not part of an activity, at least yet)
        throw new Error("Invalid operation 'announceWindow' for GW3 protocol");
    }

    public registerWindow(type: string, name: string, independent: boolean): Promise<string> {

        let shouldSendReady = typeof this._connection.gatewayToken !== "undefined";
        const peerId = this._connection.peerId;

        // if running in GD3 (glue42gd is there) we should send ready only if we have the activityInfo object in glue
        if (typeof window !== "undefined") {
            const glue42gd = window.glue42gd;
            if (glue42gd) {
                shouldSendReady = typeof glue42gd.activityInfo !== "undefined";
            }
        }

        if (shouldSendReady) {
            this._gw3Session.send({
                type: gwMessageReady,
            });
        }

        this.invokeCallbacks(
            this._activityWindowChangeCallbacks,
            new EntityEvent<ActivityWindow>(
                new ActivityWindow(peerId, name, type, undefined, this.getAgmInstance(peerId), independent, this.generateWindowGetter(peerId), undefined),
                new EntityEventContext(EntityEventType.Added)),
            "register window");

        return Promise.resolve(peerId);
    }

    public onActivityWindowChange(callback: EventHandlerCallback<EntityEvent<ActivityWindow>>) {
        this._activityWindowChangeCallbacks.push(callback);
    }

    public createWindow(activityId: string, windowDefinition: WindowDefinition): Promise<string> {

        // if the user hasn't provided a layout option but has some of left,top,width,height
        // insert a layout
        if (!windowDefinition.layout) {
            if (windowDefinition.left || windowDefinition.width || windowDefinition.height || windowDefinition.top) {
                windowDefinition.layout = {
                    mode: "pixels",
                    cellSize: 1,
                };
            }
        }

        const joinPeer = (id: string) => {
            if (!activityId) {
                return;
            }
            return this.joinActivity(activityId, id, windowDefinition.name)
                .then(() => {
                    return id;
                });
        };

        return this.sendCreateAndMapResultingMessagesToPromise(
            {
                type: gwMessageCreatePeer,
                peer_type: windowDefinition.type,
                peer_name: windowDefinition.name || windowDefinition.type,
                configuration: windowDefinition,
                activity_id: activityId,
            },
            undefined,
            undefined,
            gwMessagePeerCreated,
            (msg, requestId) => msg.request_id === requestId,
            undefined,
            undefined,
            (msg) => msg.created_id,
            joinPeer)
            .then(joinPeer);
    }

    public closeWindow(id: string): Promise<void> {
        return this._gw3Session.send({
            type: gwMessageDestroyPeer,
            destroy_peer_id: id
        }).then((_) => undefined);
    }

    public getAnnouncementInfo(): AnnouncementInfo {
        let activityId = this._config.activityId || (this._config.announcementInfo && this._config.announcementInfo.activityId);
        let activityWindowType = (this._config.announcementInfo && this._config.announcementInfo.activityWindowType);
        let activityWindowIndependent = (this._config.announcementInfo && this._config.announcementInfo.activityWindowIndependent);
        let activityWindowName = (this._config.announcementInfo && this._config.announcementInfo.activityWindowName);

        if (typeof window !== "undefined" &&
            typeof window.location !== "undefined" &&
            window.location.search &&
            typeof URLSearchParams === "function") {
            const searchParams = new URLSearchParams(location.search.slice(1));
            activityWindowType = activityWindowType || searchParams.get("t42PeerType");
            activityWindowType = activityWindowType || searchParams.get("t42ActivityWindowType");
            if (typeof activityWindowIndependent === "undefined") {
                activityWindowIndependent = searchParams.get("t42ActivityWindowIndependent");
            }
            activityWindowName = activityWindowName || searchParams.get("t42ActivityWindowName");
            activityId = activityId || searchParams.get("t42ActivityId");
        }

        activityWindowType = activityWindowType || "unknown";
        activityWindowIndependent = activityWindowIndependent || false;
        activityWindowName = activityWindowName || this._connection.peerId;

        return {
            activityWindowId: undefined, // not member of activity from the get go
            activityId,
            activityWindowType,
            activityWindowIndependent,
            activityWindowName,
        };
    }

    public joinActivity(activityId: string, windowId: string, name?: string): Promise<void> {
        const maybeName = (name && { name }) || {};
        return this._gw3Session.send({
            type: gwMmessageJoinActivity,
            target_id: windowId,
            activity_id: activityId,
            ...maybeName
        }).then(() => {
            this.invokeCallbacks(
                this._activityWindowChangeCallbacks,
                new EntityEvent<ActivityWindow>(
                    new ActivityWindow(windowId, undefined, undefined, activityId, this.getAgmInstance(windowId), undefined, this.generateWindowGetter(windowId), undefined),
                    new EntityEventContext(EntityEventType.ActivityWindowJoinedActivity)),
                "activity joined - ActivityWindow");

            this.invokeCallbacks(
                this._activityChangeCallbacks,
                new EntityEvent<Activity>(
                    new Activity(activityId, undefined, new ActivityStatus("created", undefined, undefined), undefined, undefined),
                    new EntityEventContext(EntityEventType.Updated)),
                "activity joined - Activity");
        });
    }

    public leaveActivity(activityId: string, windowId: string): Promise<void> {
        return this._gw3Session.send({
            type: gwMessageLeaveActivity,
            target_id: windowId,
            activity_id: activityId
        }).then(() => {
            this.invokeCallbacks(
                this._activityWindowChangeCallbacks,
                new EntityEvent<ActivityWindow>(
                    new ActivityWindow(windowId, undefined, undefined, null, this.getAgmInstance(windowId), undefined, this.generateWindowGetter(windowId), undefined),
                    new EntityEventContext(EntityEventType.ActivityWindowLeftActivity)),
                "activity left - ActivityWindow");

            this.invokeCallbacks(
                this._activityChangeCallbacks,
                new EntityEvent<Activity>(
                    new Activity(activityId, undefined, new ActivityStatus("created", undefined, undefined), undefined, undefined),
                    new EntityEventContext(EntityEventType.Updated)),
                "activity left - Activity");
        });
    }

    // purposefully returning empty arrays
    public getActivityTypes(): Promise<ActivityType[]> {
        return Promise.resolve([]);
    }

    public getWindowTypes(): Promise<WindowType[]> {
        return Promise.resolve([]);
    }

    public getActivities(): Promise<Activity[]> {
        return Promise.resolve([]);
    }

    public getActivityWindows(): Promise<ActivityWindow[]> {
        return Promise.resolve([]);
    }

    // N/A

    // ignore for now

    // POSTPONED
    public createStackedWindows(id: string, windowDefinitions: WindowDefinition[], timeout: number): Promise<string[]> {
        return undefined;
    }

    // POSTPONED
    public getWindowBounds(id: string): Promise<WindowBounds> {
        return undefined;
    }

    // POSTPONED
    public setWindowBounds(id: string, bounds: WindowBounds): Promise<void> {
        return undefined;
    }

    // POSTPONED
    public activateWindow(id: string, focus: boolean): Promise<void> {
        return undefined;
    }

    // POSTPONED
    public setWindowVisibility(id: string, visible: boolean): Promise<void> {
        return undefined;
    }

    // POSTPONED
    public cloneActivity(id: string, cloneOptions: CloneOptions): Promise<string> {
        return undefined;
    }

    // POSTPONED
    public attachActivities(from: string, to: string, tag: object): Promise<any> {
        return this._gw3Session.send({
            type: gwNmessageMergeActivities,
            into: to,
            merge: from
        });
    }

    // POSTPONED
    public detachActivities(activityId: string, newActivityInfo: ActivityDescriptor): Promise<string> {
        return this._gw3Session.send({
            type: gwMessageSplitActivities,
            from: activityId,
            // split: from
        }).then(() => "");
    }

    // POSTPONED
    public onActivitiesAttached(callback: (info: any) => void) {
        // POSTPONED
    }

    // POSTPONED
    public onActivitiesDetached(callback: (info: any) => void) {
        // POSTPONED
    }

    public onActivityAttachedDescriptorsRefreshed(callback: (info: any) => void): void {
        // POSTPONED
    }

    // POSTPONED
    public getAttachedDescriptors(): Promise<any[]> {
        return Promise.resolve([]);
    }

    // helpers

    // Protocol handling

    private getRandomRequestId() {
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        return this._connection.peerId + ":" + Math.floor(Math.random() * 1e9) + "";
    }

    private forwardAddedAndRemovedMessagesToEventHandler<TMessage, TEntity>(
        addedMessageType: string,
        removedMessageType: string,
        mapper: (msg: TMessage, isAdded: boolean) => TEntity[],
        handlers: EventHandlerCallback<EntityEvent<TEntity>>[]) {

        const getGetEntityEvent = (isAdded: boolean) => (entity: any) => new EntityEvent<TEntity>(
            entity,
            new EntityEventContext(
                isAdded ?
                    EntityEventType.Added :
                    EntityEventType.Removed));


        const sub1 = addedMessageType && this.forwardMessageToEventHandler<TMessage, TEntity>(
            addedMessageType,
            (msg) => mapper(msg, true),
            getGetEntityEvent(true),
            handlers);

        const sub2 = removedMessageType && this.forwardMessageToEventHandler<TMessage, TEntity>(
            removedMessageType,
            (msg) => mapper(msg, false),
            getGetEntityEvent(false),
            handlers);

        return [sub1, sub2].filter((x) => x);
    }

    private forwardMessageToEventHandler<TMessage, TEntity>(
        messageType: string,
        mapper: (msg: TMessage) => TEntity[],
        getEntityEvent: (entity: TEntity, msg: TMessage) => EntityEvent<TEntity>,
        handler: EventHandlerCallback<EntityEvent<TEntity>>[]) {
        return this.subscribe(
            messageType,
            (msg: TMessage) => {
                mapper(msg)
                    .forEach((ent: any) =>
                        handler.forEach((h) => h(getEntityEvent(ent, msg))));
            });
    }

    private sendCreateAndMapResultingMessagesToPromise<TCreatedMessage extends GwMessage, TPromiseType>(
        msg: any,
        initiatedMessageType: string,
        initiatedMessageFilter: (msg: any, requestId: string) => boolean,
        createdMessageType: string,
        createdMessageFilter: (msg: any, requestId: string, initMsg: any) => boolean,
        cancelledMessageType: string,
        cancelledMessageFilter: (msg: any, requestId: string, initMsg: any) => boolean,
        createdMessageToPromiseResolution: (TCreatedMessage: any) => TPromiseType,
        listenForRecreates: (result: TPromiseType) => void): Promise<TPromiseType> {

        const reqId = this.getRandomRequestId();

        let resolveCreatedPromise: (arg: any) => void;
        let rejectCreatedPromise: (arg: any) => void;
        const createdPromise = new Promise<TPromiseType>((resolve, reject) => {
            resolveCreatedPromise = resolve;
            rejectCreatedPromise = reject;
        });

        let initiatedMessageAck: any = null;

        let initiatedSubscription: any;
        let createdSubscription: any;
        let cancelledSubscription: any;
        let errorSubscription: any;

        const dropSubscriptions = () => {
            this.dropSubscription(initiatedSubscription);
            if (!listenForRecreates) {
                this.dropSubscription(createdSubscription);
            }
            this.dropSubscription(cancelledSubscription);
            this.dropSubscription(errorSubscription);
        };

        initiatedSubscription = initiatedMessageType &&
            this.subscribe(
                initiatedMessageType,
                (msg4: any) => {
                    if (!initiatedMessageFilter(msg4, reqId)) {
                        return;
                    }
                    initiatedMessageAck = msg4;
                    this.dropSubscription(initiatedSubscription);
                });

        let recreated = false;
        createdSubscription =
            this.subscribe(
                createdMessageType,
                (msg1: TCreatedMessage) => {
                    if (!createdMessageFilter(msg1, reqId, initiatedMessageAck)) {
                        return;
                    }
                    if (recreated) {
                        if (listenForRecreates) {
                            listenForRecreates(createdMessageToPromiseResolution(msg1));
                        }
                    } else {
                        recreated = true;
                        resolveCreatedPromise(createdMessageToPromiseResolution(msg1));
                    }
                });
        cancelledSubscription = cancelledMessageType &&
            this.subscribe(
                cancelledMessageType,
                (msg2: any) => {
                    if (!cancelledMessageFilter(msg2, reqId, initiatedMessageAck)) {
                        return;
                    }
                    rejectCreatedPromise(msg2);
                });
        errorSubscription = cancelledMessageType &&
            this.subscribe(
                gwMmessageError,
                (msg3: any) => {
                    if (msg3.request_id !== reqId) {
                        return;
                    }
                    rejectCreatedPromise(msg3);
                });

        msg.request_id = reqId;

        const toReturn = this._gw3Session
            .send(msg)
            .then(() => {
                return createdPromise;
            });
        toReturn.then(dropSubscriptions, dropSubscriptions);
        return toReturn;
    }

    // Entity Mapping

    private peerFactoryIdAndOwnerIdToWindowType(factoryId: string, ownerId: string) {
        const peerType = this._peerIdAndFactoryIdToPeerType[ownerId + ":" + factoryId];
        if (!peerType) {
            return null;
        } else {
            return new WindowType(peerType, undefined);
        }
    }

    private subscribe<TMessage>(messageType: string, handler: (msg: TMessage) => void) {
        const sub = this._connection.on(
            messageType,
            (msg: TMessage) => handler.bind(this)(msg));

        this._gw3Subscriptions.push(sub);

        return sub;
    }

    private dropSubscription(subscription: any) {
        if (subscription) {
            this._connection.off(subscription);
            delete this._gw3Subscriptions[this._gw3Subscriptions.indexOf(subscription)];
        }
    }

    private invokeCallbacks<TEvent>(
        callbacks: EventHandlerCallback<EntityEvent<TEvent>>[],
        event: EntityEvent<TEvent>,
        description?: string) {
        callbacks.forEach((cb) => {
            try {
                cb(event);
            } catch (err) {
                this._logger.error(`Error in ${description || event.context.type} callback: ` + JSON.stringify(err));
            }
        });
    }

    private handleActivityCreatedMessage(msg: ActivityGwMessage) {
        if (!msg.context_id) {
            this._logger.error("Activity created with unknown context_id: " + msg.activity_id);
        } else {
            if (!this._contextSubscriptions[msg.activity_id]) {
                this.subscribeToContext(msg);
            }
        }
    }

    private async subscribeToContext(msg: ActivityGwMessage): Promise<void> {
        const activityId = msg.activity_id;
        this._contextSubscriptions[activityId] =
            await this._contexts.subscribe(activityId,
                (data: any, updated: any, removed: string[]) => {
                    const event = new EntityEvent<Activity>(
                        new Activity(activityId, undefined, undefined, data, undefined),
                        new ActivityContextChangedEventContext(data, updated, removed));

                    this.invokeCallbacks(
                        this._activityChangeCallbacks,
                        event,
                        "context updated");
                });
    }

    private handleActivityDestroyedMessage(msg: ActivityGwMessage) {
        const unsubscribeContext = this._contextSubscriptions[msg.activity_id];
        if (typeof unsubscribeContext === "function") {
            unsubscribeContext();
        }
        delete this._contextSubscriptions[msg.activity_id];
    }

    private handlePeerFactoriesAdded(msg: PeerFactoryGwMessage) {
        msg.factories.forEach((entity: PeerFactoryGwMessageEntity) => {
            this._peerIdAndFactoryIdToPeerType[msg.owner_id + ":" + entity.id] = entity.peer_type;
        });
    }

    private handlePeerFactoriesRemoved(msg: PeerFactoryGwMessage) {
        msg.factory_ids.forEach((factoryId: string) => {
            delete this._peerIdAndFactoryIdToPeerType[msg.owner_id + ":" + factoryId];
        });
    }

    private forwardActivityTypeMessagesToStatusEventHandlers() {
        this.forwardAddedAndRemovedMessagesToEventHandler<ActivityTypeGwMessage, ActivityType>(
            gwMmessageActivityTypesAdded,
            gwMessageActivityTypesRemoved,
            (msg: ActivityTypeGwMessage, isAdded) => isAdded
                ? msg.types.map((t) => GW3Bridge.activityTypeGwMessageEntityToActivityType(t, undefined))
                : msg.types.map((t) => new ActivityType(t.name, undefined, undefined, undefined)),
            this._activityTypeStatusChangeCallbacks);

    }

    private forwardActivityCreatedAndJoinedActivityToActivityWindowEventHandlers() {
        // if we're created as part of this activity, the JOINED ACTIVITY is

        // the first time we'll hear about it; so let's record the participants
        for (const activityCreatedMessage of [gwMessageActivityCreated, gwMessageJoinedActivity, gwMessageOwnerChanged]) {
            this.forwardMessageToEventHandler<ActivityGwMessage, ActivityWindow>(
                activityCreatedMessage,
                (msg) =>
                    ([msg.owner || { ...msg, type: msg.peer_type, name: msg.peer_name, peer_id: msg.owner_id }])
                        .concat(msg.participants || [])
                        .map((info: any) => new ActivityWindow(info.peer_id, info.name, info.type, msg.activity_id, this.getAgmInstance(info.peer_id), undefined, this.generateWindowGetter(info.peer_id), undefined)),
                (ent, msg) => new EntityEvent<ActivityWindow>(ent, new EntityEventContext(EntityEventType.ActivityWindowJoinedActivity)),
                this._activityWindowChangeCallbacks);
        }
    }

    private forwardActivityMessagesToStatusEventHandlers() {

        // see dot-net-activity\Activity\ActivityState.cs

        for (const createdMessage of [gwMessageActivityCreated, gwMessageJoinedActivity]) {
            this.forwardMessageToEventHandler<ActivityGwMessage, Activity>(
                createdMessage,
                (msg) => [GW3Bridge.activityGwMessageToActivity(msg, new ActivityStatus("started", "", new Date()))],
                (ent, msg) => GW3Bridge.activityToActivityStatusChangeEvent(ent),
                this._activityChangeCallbacks);
        }

        this.forwardMessageToEventHandler<ActivityGwMessage, Activity>(
            gwMessageActivityDestroyed,
            (msg) => [GW3Bridge.activityGwMessageToActivity(msg, new ActivityStatus("destroyed", msg.reason, new Date()))],
            (ent, msg) => GW3Bridge.activityToActivityStatusChangeEvent(ent),
            this._activityChangeCallbacks);

        this.forwardMessageToEventHandler<ActivityGwMessage, Activity>(
            gwMessageActivityInitiated,
            (msg) => [GW3Bridge.activityGwMessageToActivity(msg, new ActivityStatus("created", "", new Date()))],
            (ent, msg) => GW3Bridge.activityToActivityStatusChangeEvent(ent),
            this._activityChangeCallbacks);

        this.forwardMessageToEventHandler<ActivityGwMessage, Activity>(
            gwMessageOwnerChanged,
            (msg) => [GW3Bridge.activityGwMessageToActivity(msg, new ActivityStatus("created", "", new Date()))],
            (ent, msg) => GW3Bridge.activityToActivityStatusChangeEvent(ent),
            this._activityChangeCallbacks);
    }

    private forwardPeerFactoryMessagesToStatusEventHandlers() {
        this.forwardAddedAndRemovedMessagesToEventHandler<PeerFactoryGwMessage, WindowType>(
            gwMessagePeerFactoriesAdded,
            gwMessagePeerFactoriesRemoved,
            (msg: PeerFactoryGwMessage, isAdded: boolean) => isAdded
                ? msg.factories.map(GW3Bridge.peerFactoryGwMessageEntityToWindowType)
                : msg.factory_ids.map((id) => this.peerFactoryIdAndOwnerIdToWindowType(id, msg.owner_id)).filter((x) => x != null),
            this._windowTypeStatusChangeCallbacks);
    }

    private forwardPeerFactoryMessagesToPeerFactoryRequests() {
        this.subscribe(
            gwMessagePeerRequested,
            (msg: PeerRequestedGwMessage) => {
                const factory = this._peerFactoriesRegisteredByUs[msg.peer_factory];
                if (!factory) {
                    this._gw3Session.send(
                        {
                            type: gwMmessageError,
                            request_id: msg.request_id,
                            reason: `Unknown peer factory ${msg.peer_factory}`
                        });
                    return;
                }

                try {
                    const configuration = msg.configuration || {};
                    // NB: peer factories (or whoever they're delegating to) need to support whatever activity
                    // environment (GW3, HC) we're targeting when they're creating peers
                    // the manager is env agnostic, the bridge will augment the configuration with whatever's required

                    // NB: also, since peers are requested in two ways (single peer with just its configuration,
                    // or peer created as part of new activity, where the factory receives the aggregate config of
                    // all peers, combined from ADD ACTIVITY TYPE and CREATE ACTIVITY), the peer factories need to
                    // be able to handle both configuration structures intelligently.
                    configuration.gateway_token = configuration.gateway_token || msg.gateway_token;
                    configuration.peer_factory = configuration.peer_factory || msg.peer_factory;
                    const promise = factory({
                        activityId: msg.activity && msg.activity.id,
                        activityType: msg.activity && msg.activity.type,
                        type: msg.configuration && msg.configuration.type,
                        gwToken: configuration.gateway_token,
                        configuration
                    });
                    if (promise && promise.then && promise.catch) {
                        promise.catch((err) => this._gw3Session.send(
                            {
                                type: gwMmessageError,
                                request_id: msg.request_id,
                                reason: err && (err.message || JSON.stringify(err))
                            }));
                    }
                } catch (err) {
                    this._gw3Session.send(
                        {
                            type: gwMmessageError,
                            request_id: msg.request_id,
                            reason: err && (err.message || JSON.stringify(err))
                        });
                }
            });
    }

    private forwardActivityWindowMessagesToEventHandlers() {
        for (const joinedMessage of [gwMessageActivityJoined, gwMessageJoinedActivity]) {
            this.subscribe(joinedMessage,
                (msg: any) => {
                    const joinedId = (joinedMessage === gwMessageActivityJoined) ? msg.joined_id : msg.peer_id;
                    const joinedType = (joinedMessage === gwMessageActivityJoined) ? msg.joined_type : msg.peer_type;
                    const joinedName = (joinedMessage === gwMessageActivityJoined) ? msg.joined_name : msg.peer_name;

                    const entity = new ActivityWindow(
                        joinedId,
                        joinedName,
                        joinedType /* for ACTIVITY JOINED */,
                        msg.activity_id,
                        this.getAgmInstance(joinedId),
                        undefined,
                        this.generateWindowGetter(joinedId),
                        undefined);

                    // TODO review - what if it's our activity, do we
                    // even need to subscribe
                    if (!this._contextSubscriptions[msg.activity_id]) {
                        this.subscribeToContext(msg).then(() => {
                            if (joinedMessage === gwMessageJoinedActivity) {
                                this._activityJoinedPromiseResolve({});
                            }
                        });
                    } else if (joinedMessage === gwMessageJoinedActivity) {
                        this._activityJoinedPromiseResolve({});
                    }

                    // TODO review: we might have the context data here already
                    // why not update it here instead of waiting for subscribeToContext
                    this.invokeCallbacks(
                        this._activityWindowChangeCallbacks,
                        new EntityEvent<ActivityWindow>(
                            entity,
                            new EntityEventContext(EntityEventType.ActivityWindowJoinedActivity)),
                        joinedMessage);

                });
        }

        this.subscribe(
            gwMessageActivityLeft,
            (msg: any) => {
                const entity = new ActivityWindow(
                    msg.left_id,
                    undefined,
                    undefined,
                    null,
                    this.getAgmInstance(msg.left_id),
                    undefined,
                    this.generateWindowGetter(msg.left_id),
                    undefined);

                this.invokeCallbacks(
                    this._activityWindowChangeCallbacks,
                    new EntityEvent<ActivityWindow>(
                        entity,
                        new EntityEventContext(EntityEventType.ActivityWindowLeftActivity)),
                    gwMessageActivityLeft);
            });

        this.forwardAddedAndRemovedMessagesToEventHandler<PeerCreatedGwMessage, ActivityWindow>(
            gwMessagePeerCreated,
            undefined,
            (msg) => [
                new ActivityWindow(
                    msg.created_id,
                    undefined /* #deleteme TODO: review - we should know the name if any */,
                    undefined /* #deleteme TODO: review - we should know the type */,
                    undefined,
                    undefined,
                    undefined,
                    this.generateWindowGetter(msg.created_id),
                    undefined)],
            this._activityWindowChangeCallbacks);
    }

    private getAgmInstance(id: string): Glue42.AGM.Instance {
        return this._config.agm.servers().find((s) => s.peerId === id || s.windowId === id);
    }

    private generateWindowGetter(peerId: string): () => Glue42.Windows.GDWindow {
        return () => {
            const server = this.getAgmInstance(peerId);
            if (!server) {
                return;
            }
            const windowId = server.windowId;
            return this._config.windows.list().filter((w) => w.id === windowId)[0];
        };
    }

    private isOverrideTypeDefinition(value: OverrideTypeDefinition | WindowDefinition[]): value is OverrideTypeDefinition {
        if (typeof value === "undefined") {
            return false;
        }
        if ((value as OverrideTypeDefinition).owner) {
            return true;
        }
        return false;
    }
}
